import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Icon from './Icons';

/**
 * "Choose the main voices" in the start dialog. Before anything is recorded:
 *   GET {base}/cast-plan  -> {status: casting|error|ready, main[], others[], narrator, voices[]}
 * (polled while the server casts in the background). The parent sends the picks with /start as
 * `cast: {CHARACTER: voice | null, __NARRATOR__: voice}` — null = "this character's voice was given
 * to someone else: cast them again automatically".
 *
 * Main characters = the most lines (owner, 2026-10-11). Each shows the voice automatic casting
 * gave it, so Record with no changes is still a good cast. Two main characters never share a voice:
 * giving one a voice another has sends that other back to automatic.
 */
const NARR = '__NARRATOR__';

export default function CastPick({ base, value, onChange, onStatus }) {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [others, setOthers] = useState(false);
  const [sheet, setSheet] = useState(null);           // character being changed (or NARR)
  const [playing, setPlaying] = useState(null);       // sample url playing
  const [wait, setWait] = useState(null);             // {eta, since} while the server casts
  const [now, setNow] = useState(Date.now());
  useEffect(() => { onStatus?.(plan ? 'ready' : err ? 'error' : 'casting'); }, [plan, err, onStatus]);
  useEffect(() => { if (!wait) return undefined; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [wait]);
  const audio = useRef(null);
  const choices = value || {};

  const load = useCallback((retry = false, tries = 0) => {
    setErr(null);
    axios.get(`${base}/cast-plan`, { params: retry ? { retry: 1 } : {} }).then((r) => {
      if (r.data.status === 'ready') { setPlan(r.data); setWait(null); }
      else if (r.data.status === 'casting') {
        const eta = r.data.eta_seconds ?? 90, el = r.data.elapsed_seconds ?? 0;
        setWait((w) => w ?? { eta, since: Date.now() - el * 1000 });
        if (tries > 60) setErr('Casting is taking longer than usual. Try again in a minute.');
        else setTimeout(() => load(false, tries + 1), 3000);
      } else setErr(r.data.error || 'Casting failed.');
    }).catch((e) => setErr(e?.response?.data?.error ?? e.message));
  }, [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { audio.current?.pause(); }, []);

  const voices = useMemo(() => Object.fromEntries((plan?.voices ?? []).map((v) => [v.id, v])), [plan]);
  const current = useCallback((c) => {
    if (c === NARR) return choices[NARR] ?? plan?.narrator;
    if (Object.prototype.hasOwnProperty.call(choices, c)) return choices[c];     // null = automatic
    const row = [...(plan?.main ?? []), ...(plan?.others ?? [])].find((x) => x.character === c);
    return row?.voice ?? null;
  }, [choices, plan]);

  const play = (url) => {
    if (!url) return;
    if (!audio.current) { audio.current = new Audio(); audio.current.onended = () => setPlaying(null); }
    if (playing === url) { audio.current.pause(); setPlaying(null); return; }
    audio.current.src = url; audio.current.play().catch(() => {}); setPlaying(url);
  };

  const pick = (c, voiceId) => {
    const next = { ...choices, [c]: voiceId };
    if (c !== NARR) {
      for (const m of plan.main) {
        if (m.character !== c && current(m.character) === voiceId) next[m.character] = null;   // displaced
      }
    }
    onChange(next);
    setSheet(null);
  };

  if (err) return <p className="cp-msg cp-err">{err} <button type="button" className="tr-linkbtn" onClick={() => load(true)}>Try again</button></p>;
  if (!plan) {
    const el = wait ? Math.max(0, Math.round((now - wait.since) / 1000)) : 0;
    const eta = wait?.eta ?? 90;
    const pct = Math.min(95, Math.round((el / eta) * 100));
    const mm = (x) => `${Math.floor(x / 60)}:${String(x % 60).padStart(2, '0')}`;
    const about = eta < 90 ? 'about a minute' : `about ${Math.round(eta / 60)} minutes`;
    return (
      <div className="cp-wait" role="status" aria-live="polite">
        <div className="cp-wait-head"><span className="cp-spin" aria-hidden="true" /><b>Casting the characters</b><span className="cp-wait-time">{mm(el)}</span></div>
        <p>An AI reads the whole script to work out who's who — men, women, ages. Usually {about}.{el > eta ? ' Almost there…' : ''}</p>
        <div className="cp-bar"><i style={{ width: `${pct}%` }} /></div>
        <p className="cp-wait-note">You can close this window — the casting carries on and will be ready when you come back.</p>
      </div>
    );
  }

  const usedBy = (exclude) => {
    const m = {};
    for (const row of plan.main) if (row.character !== exclude) { const v = current(row.character); if (v) (m[v] ??= []).push(row.character); }
    if (exclude !== NARR) { const v = current(NARR); if (v) (m[v] ??= []).push('Narrator'); }
    return m;
  };

  const toCheck = plan.others.filter((r) => r.unsure && !Object.prototype.hasOwnProperty.call(choices, r.character)).length;

  // A plain render function, NOT a component: a component declared inside CastPick is a new type on
  // every render, so playing a sample (a state change) remounted every row and the dialog jumped to the top.
  const row = ({ c, lines, label, small, unsure }) => {
    const v = current(c), card = v ? voices[v] : null;
    const flag = unsure && !Object.prototype.hasOwnProperty.call(choices, c);   // changed = checked
    return (
      <li key={c} className={small ? 'cp-row cp-small' : 'cp-row'}>
        <div className="cp-who">
          <span className="cp-name">{label ?? c}</span>
          {lines != null && <span className="cp-lines">{lines} lines</span>}
        </div>
        {flag && <p className="cp-unsure">The script doesn't say if this is a man or a woman — check the voice.</p>}
        <div className="cp-voice">
          {card?.sample_url
            ? <button type="button" className={playing === card.sample_url ? 'cp-play on' : 'cp-play'} aria-label={`Play ${card.name}`}
                      onClick={() => play(card.sample_url)}>{playing === card.sample_url ? '❚❚' : '▶'}</button>
            : <span className="cp-play cp-ghost" aria-hidden="true" />}
          <span className="cp-vtext">
            {card ? <><b>{card.name}</b> · {card.who}{!small && card.traits && <span className="cp-traits">{card.traits}</span>}</>
              : <span className="cp-auto">Automatic — re-cast around your picks</span>}
          </span>
          <button type="button" className="tr-linkbtn cp-change" onClick={() => setSheet(c)}>Change</button>
        </div>
      </li>
    );
  };

  return (
    <div className="cp">
      <div className="cp-head"><span>Main characters</span><span>lines</span></div>
      <ul className="cp-list">
        {plan.main.map((r) => row({ c: r.character, lines: r.lines, unsure: r.unsure }))}
        {row({ c: NARR, label: 'Narrator (action & headings)' })}
      </ul>
      {plan.others.length > 0 && (
        <>
          <button type="button" className="cp-more" aria-expanded={others} onClick={() => setOthers((o) => !o)}>
            {others ? '▾' : '▸'} Everyone else ({plan.others.length}) — cast automatically{toCheck ? ` · ${toCheck} to check` : ''}
          </button>
          {others && (
            <>
              <ul className="cp-list">{plan.others.map((r) => row({ c: r.character, lines: r.lines, unsure: r.unsure, small: true }))}</ul>
              <p className="cp-note">Anything you don't change stays automatic.</p>
            </>
          )}
        </>
      )}
      {sheet && (
        <VoiceSheet who={sheet} row={[...plan.main, ...plan.others].find((r) => r.character === sheet)}
                    voices={plan.voices} current={current(sheet)} usedBy={usedBy(sheet)}
                    playing={playing} play={play} onPick={(v) => pick(sheet, v)} onClose={() => setSheet(null)} />
      )}
    </div>
  );
}

function VoiceSheet({ who, row, voices, current, usedBy, playing, play, onPick, onClose }) {
  const [sel, setSel] = useState(current);
  const [rest, setRest] = useState(false);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    addEventListener('keydown', k, true); return () => removeEventListener('keydown', k, true);
  }, [onClose]);
  const g = row?.gender;                                   // "F" | "M" | "any" | "?"
  const young = ['child', 'teen'].includes(row?.age_band);
  const fits = (v) => (g === 'F' ? v.kind === 'F' : g === 'M' ? (v.kind === 'M' || v.kind === 'young') : true);
  const first = voices.filter(fits);
  if (g === 'M' && young) first.sort((a, b) => (b.kind === 'young') - (a.kind === 'young'));
  const second = voices.filter((v) => !fits(v));
  const title = who === NARR ? 'Narrator' : who;
  const sub = who === NARR ? 'action lines and scene headings'
    : [g === 'F' ? 'female' : g === 'M' ? 'male' : null, row?.lines != null ? `${row.lines} lines` : null].filter(Boolean).join(' · ');
  const chosen = voices.find((v) => v.id === sel);

  const Opt = (v) => (
    <li key={v.id} className={sel === v.id ? 'cp-opt on' : 'cp-opt'} onClick={() => setSel(v.id)}>
      <input type="radio" name="cp-voice" checked={sel === v.id} onChange={() => setSel(v.id)} aria-label={v.name} />
      <button type="button" className={playing === v.sample_url ? 'cp-play on' : 'cp-play'} disabled={!v.sample_url}
              aria-label={`Play ${v.name}`} onClick={(e) => { e.stopPropagation(); play(v.sample_url); }}>{playing === v.sample_url ? '❚❚' : '▶'}</button>
      <span className="cp-vtext">
        <b>{v.name}</b> · {v.who}{v.id === current && <span className="cp-tag">current</span>}
        {v.traits && <span className="cp-traits">{v.traits}</span>}
        {usedBy[v.id] && <span className="cp-used">used by {usedBy[v.id].join(', ')}</span>}
      </span>
    </li>
  );

  return (
    <div className="cp-back" onClick={onClose}>
      <div className="cp-sheet" role="dialog" aria-modal="true" aria-labelledby="cp-sh-h" onClick={(e) => e.stopPropagation()}>
        <div className="cp-sheet-head">
          <div><h3 id="cp-sh-h">Voice for {title}</h3>{sub && <p>{sub}</p>}</div>
          <button type="button" className="cp-x" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="cp-sheet-body">
          {g === 'F' || g === 'M' ? <p className="cp-group">{g === 'F' ? 'Female voices' : 'Male voices'}</p> : <p className="cp-group">All voices</p>}
          <ul className="cp-opts">{first.map(Opt)}</ul>
          {second.length > 0 && (
            <>
              <button type="button" className="cp-more" aria-expanded={rest} onClick={() => setRest((r) => !r)}>
                {rest ? '▾' : '▸'} Other voices ({second.length})
              </button>
              {rest && <ul className="cp-opts">{second.map(Opt)}</ul>}
            </>
          )}
        </div>
        <div className="cp-sheet-foot">
          <button type="button" className="tr-btn tr-play" disabled={!sel || sel === current} onClick={() => onPick(sel)}>
            {sel && sel !== current ? `Use ${chosen?.name ?? 'this voice'}` : 'Pick a voice'}
          </button>
        </div>
      </div>
    </div>
  );
}

export const CASTPICK_CSS = `
.cp-msg{color:#46657a;font-size:.92rem;display:flex;align-items:center;gap:8px;margin:6px 0}
.cp-err{color:#a4243b}
.cp-spin{width:14px;height:14px;border:2px solid #cfdde4;border-top-color:#14536e;border-radius:50%;animation:cpspin .8s linear infinite}
@keyframes cpspin{to{transform:rotate(360deg)}}
.cp-head{display:flex;justify-content:space-between;font-size:.72rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#46657a;margin:6px 2px 4px}
.cp-list{list-style:none;margin:0;padding:0;border:1px solid #e3edf1;border-radius:10px;overflow:hidden}
.cp-row{padding:10px 12px;border-top:1px solid #e3edf1}
.cp-row:first-child{border-top:0}
.cp-who{display:flex;justify-content:space-between;align-items:baseline;gap:8px}
.cp-name{font-weight:700;color:#102d38;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cp-lines{font-size:.85rem;color:#46657a;white-space:nowrap}
.cp-voice{display:flex;align-items:flex-start;gap:8px;margin-top:4px}
.cp-small{padding:7px 12px}.cp-small .cp-voice{margin-top:2px}.cp-small .cp-name{font-weight:600;font-size:.92rem}
.cp-play{flex:0 0 auto;width:28px;height:28px;border-radius:50%;border:1px solid #cfdde4;background:#fff;color:#14536e;cursor:pointer;font-size:.72rem;line-height:1;display:inline-flex;align-items:center;justify-content:center;padding:0}
.cp-play.on{background:#14536e;color:#fff;border-color:#14536e}
.cp-play:disabled{opacity:.4;cursor:default}
.cp-ghost{border-color:transparent;cursor:default}
.cp-vtext{flex:1;min-width:0;font-size:.92rem;color:#102d38;line-height:1.35}
.cp-traits{display:block;color:#46657a;font-size:.85rem}
.cp-auto{color:#46657a;font-style:italic}
.cp-change{flex:0 0 auto;font-size:.88rem;padding:4px 2px}
.cp-more{border:0;background:none;color:#14536e;font:inherit;font-size:.92rem;cursor:pointer;padding:10px 2px 6px}
.cp-note{font-size:.82rem;color:#46657a;margin:6px 2px 0}
.cp-wait{border:1.5px solid #cfdde4;border-radius:12px;padding:14px 16px;margin:10px 0 4px;background:#f6fafc}
.cp-wait-head{display:flex;align-items:center;gap:10px;color:#102d38}
.cp-wait-head b{flex:1}
.cp-wait-time{font-variant-numeric:tabular-nums;color:#46657a;font-size:.9rem}
.cp-wait p{margin:8px 0 0;color:#46657a;font-size:.9rem;line-height:1.45}
.cp-wait .cp-wait-note{font-size:.82rem}
.cp-bar{height:6px;border-radius:3px;background:#e3edf1;overflow:hidden;margin-top:10px}
.cp-bar i{display:block;height:100%;background:#14536e;border-radius:3px;transition:width 1s linear}
.cp-unsure{margin:2px 0 0;font-size:.8rem;color:#7a4e00}
.cp-back{position:fixed;inset:0;background:rgba(16,45,56,.45);display:flex;align-items:center;justify-content:center;padding:16px;z-index:2147483001}
.cp-sheet{background:#fff;border-radius:12px;width:100%;max-width:480px;max-height:calc(100vh - 32px);display:flex;flex-direction:column;box-shadow:0 20px 50px -20px rgba(16,45,56,.5)}
.cp-sheet-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:16px 18px 8px}
.cp-sheet-head h3{margin:0;font-size:1.1rem;color:#102d38}.cp-sheet-head p{margin:2px 0 0;color:#46657a;font-size:.88rem}
.cp-x{border:0;background:none;font-size:1.1rem;color:#46657a;cursor:pointer;padding:2px 4px}
.cp-sheet-body{overflow-y:auto;padding:0 18px}
.cp-group{font-size:.72rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#46657a;margin:6px 0 4px}
.cp-opts{list-style:none;margin:0;padding:0}
.cp-opt{display:flex;align-items:flex-start;gap:10px;padding:9px 6px;border-radius:8px;cursor:pointer}
.cp-opt.on{background:#eef5f8}
.cp-opt input{margin-top:6px;accent-color:#14536e}
.cp-tag{margin-left:6px;font-size:.72rem;color:#14536e;border:1px solid #cfdde4;border-radius:999px;padding:0 6px}
.cp-used{display:block;font-size:.8rem;color:#7a4e00}
.cp-sheet-foot{padding:12px 18px 16px;border-top:1px solid #e3edf1}
.cp-sheet-foot .tr-btn{width:100%}
@media (max-width:520px){.cp-back{align-items:flex-end;padding:0}.cp-sheet{max-width:none;border-radius:14px 14px 0 0;max-height:85vh}}
`;
